import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import searchIcon from '../../assets/icons/search.svg';
import { useAuth } from '@/contexts/AuthContext';
import { INPUT_THEMES, type InputTheme } from './form/inputThemes';
import { supabase } from '@/integrations/supabase/client';

interface Instructor {
  id: string;
  full_name: string;
  username?: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface InstructorAutocompleteProps {
  value: Instructor | string | null;
  onChange: (instructor: Instructor | string | null) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  refreshKey?: number; // Key to trigger refresh of manual instructors list
  /** Visual theme: 'dark' or 'light'. Defaults to 'dark'. */
  theme?: InputTheme;
  /**
   * When true, blurring the input without selecting a dropdown item will NOT
   * auto-commit the typed text as a manual instructor. Use this in contexts
   * like EditInstructorsModal where every addition must be an explicit click.
   */
  disableBlurAdd?: boolean;
  /**
   * When true, the dropdown is rendered inline (no createPortal) so it stays
   * inside the Dialog's DOM tree and avoids Radix FocusTrap blocking clicks.
   */
  noPortal?: boolean;
}

export function InstructorAutocomplete({
  value,
  onChange,
  placeholder = "Type a name or a username",
  className = "",
  containerClassName = "",
  disabled = false,
  refreshKey = 0,
  theme = 'dark',
  disableBlurAdd = false,
  noPortal = false,
}: InstructorAutocompleteProps) {
  const { user } = useAuth();
  const tk = INPUT_THEMES[theme];
  const containerRef = useRef<HTMLDivElement>(null);
  const [filteredInstructors, setFilteredInstructors] = useState<Instructor[]>([]);
  const [savedManualInstructors, setSavedManualInstructors] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showManualOption, setShowManualOption] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);

  // Ref-based selection guard — immune to stale closures. When the user clicks
  // a dropdown item, React batches the state updates (open→false, inputValue→full
  // name) but they aren't applied until after the event handler returns. If blur
  // fires in that gap (common on mobile/touch), a closure-captured useState value
  // would still see open=true and inputValue="partial text", causing the blur
  // handler to incorrectly call onChange("partial text") as a manual instructor.
  // A ref is always current regardless of when the closure was created.
  const justSelectedRef = useRef(false);

  // Extract display value from the value prop
  const displayValue = typeof value === 'string' ? value : (value && typeof value === 'object' ? value.full_name || '' : '');

  useEffect(() => {
    // Only sync displayValue → inputValue when no selection is in progress.
    // justSelectedRef is used (ref, not state) to avoid stale closure issues.
    if (!justSelectedRef.current) {
      setInputValue(displayValue || '');
    }
  }, [displayValue]);

  // Fetch saved manual instructors from profile when component mounts, user changes, or refreshKey changes
  useEffect(() => {
    const fetchSavedManualInstructors = async () => {
      if (!user?.id) return;
      
      try {
        const { data: result, error } = await supabase.functions.invoke('get_profile_instructors', {
          body: { profile_id: user.id, all: true },
        });

        if (!error && result) {
          const manualNames = (result.instructors || [])
            .filter((inst: any) => inst.is_manual && inst.manual_name)
            .map((inst: any) => inst.manual_name);
          setSavedManualInstructors(manualNames);
        }
      } catch (error) {
        console.error('Error fetching saved manual instructors:', error);
      }
    };

    fetchSavedManualInstructors();
  }, [user?.id, refreshKey]);

  // Calculate dropdown position whenever it opens (Portal needs fixed coords)
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [open, filteredInstructors.length, showManualOption]);

  useEffect(() => {
    const fetchInstructors = async () => {
      // Allow manual option even with 1 character for better UX and testability
      if (!inputValue || inputValue.length < 1) {
        setFilteredInstructors([]);
        setShowManualOption(false);
        return;
      }
      
      // Only fetch from API if we have at least 2 characters
      if (inputValue.length < 2) {
        // For single character, show saved manual instructors that match
        const matchingManualInstructors = savedManualInstructors
          .filter(name => name.toLowerCase().includes(inputValue.toLowerCase()))
          .map(name => ({
            id: `manual-${name}`,
            full_name: name,
            username: '',
            profile_picture_url: undefined,
            belt_rank: 'black' // Manual instructors default to black belt
          }));
        
        setFilteredInstructors(matchingManualInstructors);
        setShowManualOption(matchingManualInstructors.length === 0);
        setOpen(true);
        return;
      }
      
      setLoading(true);
      try {
        const { data: result, error: invokeError } = await supabase.functions.invoke('search_instructors', {
          body: { searchTerm: inputValue, limit: 10 },
        });

        if (!invokeError && result) {
          const parsed = typeof result === 'string' ? JSON.parse(result) : result;
          let instructors = parsed.data || [];
          // Remove duplicates by id
          const seen = new Set();
          instructors = instructors.filter((inst: Instructor) => {
            if (seen.has(inst.id)) return false;
            seen.add(inst.id);
            return true;
          });
          
          // Add saved manual instructors that match the input
          const matchingManualInstructors = savedManualInstructors
            .filter(name => name.toLowerCase().includes(inputValue.toLowerCase()))
            .map(name => ({
              id: `manual-${name}`,
              full_name: name,
              username: '',
              profile_picture_url: undefined,
              belt_rank: 'black' // Manual instructors default to black belt
            }));
          
          // Combine and remove duplicates
          const allInstructors = [...instructors, ...matchingManualInstructors];
          const uniqueInstructors = allInstructors.filter((inst, index, self) =>
            index === self.findIndex(i => i.id === inst.id || i.full_name === inst.full_name)
          );
          
          setFilteredInstructors(uniqueInstructors);
          
          // Show manual option if no exact matches found
          const hasExactMatch = uniqueInstructors.some(
            (instructor: Instructor) => 
              (instructor.full_name?.toLowerCase() === inputValue.toLowerCase()) ||
              (instructor.username?.toLowerCase() === inputValue.toLowerCase())
          );
          setShowManualOption(!hasExactMatch && inputValue.length > 0);
        } else {
          console.error('Error fetching instructors:', invokeError);
          // Even if API fails, show saved manual instructors that match
          const matchingManualInstructors = savedManualInstructors
            .filter(name => name.toLowerCase().includes(inputValue.toLowerCase()))
            .map(name => ({
              id: `manual-${name}`,
              full_name: name,
              username: '',
              profile_picture_url: undefined,
              belt_rank: 'black' // Manual instructors default to black belt
            }));
          
          setFilteredInstructors(matchingManualInstructors);
          setShowManualOption(matchingManualInstructors.length === 0 && inputValue.length > 0);
        }
      } catch (error) {
        console.error('Error fetching instructors:', error);
        // Even if API fails, show saved manual instructors that match
        const matchingManualInstructors = savedManualInstructors
          .filter(name => name.toLowerCase().includes(inputValue.toLowerCase()))
          .map(name => ({
            id: `manual-${name}`,
            full_name: name,
            username: '',
            profile_picture_url: undefined,
            belt_rank: 'black' // Manual instructors default to black belt
          }));
        
        setFilteredInstructors(matchingManualInstructors);
        setShowManualOption(matchingManualInstructors.length === 0 && inputValue.length > 0);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchInstructors, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue, savedManualInstructors]);

  const handleSelect = (instructor: Instructor | string) => {
    // Mark selection immediately via ref so handleInputBlur always sees the
    // correct value even if blur fires before React re-renders (stale closure).
    justSelectedRef.current = true;

    if (typeof instructor === 'object' && instructor.id?.startsWith('manual-')) {
      const manualName = instructor.full_name || instructor.id.replace('manual-', '');
      onChange(manualName);
      setInputValue(manualName);
    } else {
      onChange(instructor);
      setInputValue(typeof instructor === 'string' ? instructor : instructor.full_name || '');
    }
    setOpen(false);
    setShowManualOption(false);
    // Keep the guard active longer than the blur setTimeout (300ms) so any
    // delayed blur that fires after the click is still blocked.
    setTimeout(() => { justSelectedRef.current = false; }, 400);
  };

  const handleManualAdd = () => {
    // Prevent blur from interfering with the selection
    // Set a flag to indicate manual selection is in progress
    handleSelect(inputValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Always open dropdown when typing (for better UX and testability)
    if (newValue.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
    
    // Clear selection if input is cleared
    if (!newValue) {
      onChange(null);
    }
  };

  const handleInputFocus = () => {
    // Refresh manual instructors list when input receives focus
    // This ensures newly saved manual instructors appear in the search
    if (user?.id) {
      const fetchSavedManualInstructors = async () => {
        try {
          const { data: result, error } = await supabase.functions.invoke('get_profile_instructors', {
            body: { profile_id: user.id, all: true },
          });

          if (!error && result) {
            const manualNames = (result.instructors || [])
              .filter((inst: any) => inst.is_manual && inst.manual_name)
              .map((inst: any) => inst.manual_name);
            setSavedManualInstructors(manualNames);
          }
        } catch (error) {
          console.error('Error fetching saved manual instructors on focus:', error);
        }
      };
      
      fetchSavedManualInstructors();
    }
    
    // Always open dropdown on focus if there's any input value
    // This makes it more testable and user-friendly
    if (inputValue.length >= 2) {
      setOpen(true);
    } else if (inputValue.length > 0) {
      // Even with 1 character, open if we want to show manual option
      setOpen(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      // justSelectedRef is a ref (not state) so it always reflects the latest
      // value — no stale closure risk even if blur fires before React re-renders.
      if (justSelectedRef.current) return;

      if (!open) return;

      setOpen(false);

      // When disableBlurAdd is true, only explicit dropdown clicks add instructors.
      // This avoids the race condition in modal contexts (e.g. EditInstructorsModal)
      // where value={null} would always trigger onChange with whatever was typed.
      if (disableBlurAdd) return;

      // Only auto-accept typed text as manual if no explicit selection was made
      if (inputValue && inputValue.length > 0) {
        const currentValueIsString = typeof value === 'string';
        const currentValueMatches = currentValueIsString && value === inputValue;
        const currentValueIsObject = value && typeof value === 'object';
        const objectValueMatches = currentValueIsObject && (value.full_name === inputValue || (value as any).name === inputValue);

        if (!currentValueMatches && !objectValueMatches) {
          onChange(inputValue);
        }
      }
    }, 300);
  };

  return (
    <div ref={containerRef} className={cn("relative", containerClassName)}>
      <div className="relative">
        <img
          src={searchIcon}
          alt="Search"
          className={cn(
            "absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5",
            theme === 'light'
              ? "brightness-0 opacity-40"
              : "brightness-0 invert opacity-60"
          )}
        />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          data-testid="instructor-autocomplete-input"
          className={cn(
            "w-full border h-12 rounded-md pl-12 pr-12 text-sm focus:outline-none transition-all duration-200 font-sans",
            tk.input.split(' ').filter(cls => !cls.startsWith('h-') && !cls.startsWith('text-') && !cls.startsWith('font-')).join(' '),
            className
          )}
          style={{
            zIndex: 1,
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        />
        {inputValue && (
          <button
            onClick={() => {
              setInputValue('');
              onChange(null);
            }}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200",
              theme === 'light'
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/60 hover:text-white"
            )}
            aria-label="Clear search"
            style={{ zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {loading && !inputValue && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ zIndex: 2 }}>
            <div className={cn(
              "w-4 h-4 border-2 rounded-full animate-spin",
              theme === 'light'
                ? "border-gray-300 border-t-gray-600"
                : "border-white/30 border-t-white"
            )} />
          </div>
        )}
      </div>

      {/* Dropdown — rendered inline (noPortal) or via Portal depending on context.
          Inside a Radix Dialog, use noPortal=true to keep the dropdown within the
          Dialog's DOM tree and avoid the FocusTrap blocking pointer events. */}
      {open && inputValue.length > 0 && (filteredInstructors.length > 0 || showManualOption) && (() => {
        const dropdownContent = (
          <div
            className={['rounded-md shadow-lg max-h-60 overflow-y-auto', tk.dropdown].join(' ')}
            data-instructor-dropdown
            data-testid="instructor-dropdown"
            style={noPortal ? {
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              zIndex: 9999,
              marginTop: 4,
            } : {
              position: 'fixed',
              top: dropdownRect?.top,
              left: dropdownRect?.left,
              width: dropdownRect?.width,
              zIndex: 9999,
            }}
          >
            {loading ? (
              <div className={tk.helperText + ' p-4 text-center'}>
                Loading instructors...
              </div>
            ) : (
              <div className="py-1">
                {filteredInstructors.map((instructor) => (
                  <div
                    key={instructor.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(instructor)}
                    onTouchStart={(e) => e.preventDefault()}
                    onTouchEnd={() => handleSelect(instructor)}
                    className={cn('relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2', tk.dropdownItem)}
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {inputValue === instructor.full_name && <Check className={cn('h-4 w-4', tk.dropdownItemText)} />}
                    </span>
                    <div className="flex flex-col flex-1">
                      <span className={cn('text-sm font-medium', tk.dropdownItemText)}>{instructor.full_name || 'Unknown'}</span>
                      <span className={cn('text-xs', tk.helperText)}>@{instructor.username || 'unknown'}</span>
                    </div>
                  </div>
                ))}

                {showManualOption && (
                  <div
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleManualAdd()}
                    onTouchStart={(e) => e.preventDefault()}
                    onTouchEnd={() => handleManualAdd()}
                    data-testid="add-manually-option"
                    data-instructor-name={inputValue}
                    className={cn('relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 border-t', theme === 'light' ? 'border-gray-200' : 'border-white/20', tk.dropdownItem)}
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Plus className={cn('h-4 w-4', tk.dropdownItemText)} />
                    </span>
                    <div className="flex flex-col flex-1">
                      <span
                        className={cn('text-sm font-medium', tk.dropdownItemText)}
                        data-testid="add-manually-text"
                      >
                        Add "{inputValue}" manually
                      </span>
                      <span className={cn('text-xs', tk.helperText)}>Manual instructor</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

        return noPortal ? dropdownContent : (dropdownRect ? createPortal(dropdownContent, document.body) : null);
      })()}
    </div>
  );
};